import { Box, Button, Stack, Typography } from '@mui/material'
import {
  Control,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { Select, TextInputField } from 'utoniq-ui'

import { MembershipInterval } from '@/clients/utoniq-core/schema'
import {
  CreateMembershipProps,
  monthlyFees,
} from '@/components/pages/ConsoleMembershipCreate/template/MembershipCreateForm'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type PriceInputCellProps = {
  watch: UseFormWatch<CreateMembershipProps>
  setValue: UseFormSetValue<CreateMembershipProps>
  control: Control<CreateMembershipProps>
  register: UseFormRegister<CreateMembershipProps>
  hasYearlyPlan: boolean
}

export const PriceInputCell: Cell<PriceInputCellProps> = ({
  watch,
  setValue,
  control,
  register,
  hasYearlyPlan,
}) => {
  const { t } = useTranslation('consoleMembershipCreate')

  const { fields: planFields } = useFieldArray({ control, name: 'plans' })

  const options = [
    { value: true, label: t('yearlyPaymentAvailable', '年払いあり') },
    { value: false, label: t('yearlyPaymentUnavailable', '年払いなし') },
  ]

  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h5">{t('feeLabel', '会費')}</Typography>
      <Typography
        component="p"
        variant="caption"
        color="text.secondary"
        sx={{ pt: 2 }}
      >
        {t(
          'feeDesc',
          'メンバーシップの会費を入力してください。月額費はクレジットカード決済のみ、年額費はコンビニ決済とクレジットカード決済が可能です。'
        )}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ py: 2 }}>
        {options.map((option, index) => (
          <Button
            key={index}
            variant={hasYearlyPlan === option.value ? 'contained' : 'outlined'}
            sx={{
              color: hasYearlyPlan === option.value ? 'white' : 'text.hint',
            }}
            onClick={() => {
              if (hasYearlyPlan) {
                setValue(
                  'plans',
                  [
                    {
                      interval: MembershipInterval.Month,
                      amount: 480,
                    },
                  ] as never,
                  {
                    shouldValidate: true,
                    shouldDirty: true,
                  }
                )
                setValue('annualContent', null as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              } else {
                setValue(
                  'plans',
                  [
                    {
                      interval: MembershipInterval.Month,
                      amount: 480,
                      membershipNumberPrefix: null,
                    },
                    {
                      interval: MembershipInterval.Year,
                      amount: 480 * 12,
                      membershipNumberPrefix: null,
                    },
                  ] as never,
                  {
                    shouldValidate: true,
                    shouldDirty: true,
                  }
                )
              }
            }}
          >
            {option.label}
          </Button>
        ))}
      </Stack>
      {planFields.map((field, index) => (
        <Stack pt={2} key={field.id} spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {field.interval === MembershipInterval.Month ? (
              <Stack>
                <Typography variant="subtitle1">
                  {t(`${field.interval}lyFee`)}
                </Typography>
                <Select
                  listItems={monthlyFees}
                  value={watch(`plans.${index}.amount`)?.toString()}
                  onChange={value => setValue(`plans.${index}.amount`, +value)}
                  placeholder={t('unselected')}
                  formControlProps={{
                    sx: { minWidth: 164, width: 164, pt: 2 },
                  }}
                />
              </Stack>
            ) : (
              <Box>
                <TextInputField
                  type="number"
                  t={t}
                  titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
                  {...register(`plans.${index}.amount`)}
                  textFieldProps={{
                    sx: { minWidth: 164, width: 164, mb: 0 },
                    FormHelperTextProps: { sx: { display: 'none' } },
                  }}
                />
              </Box>
            )}
            <Typography
              variant="subtitle1"
              pt={field.interval === MembershipInterval.Month ? 6 : 7}
            >
              {t('yen')}
            </Typography>
          </Stack>
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary">
        {t('feeCaution')}
      </Typography>
    </CellFrame>
  )
}
